import React, { useEffect, useState } from "react";
import { FaUserShield, FaCheck } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import styles from "./styles/UserManagement.module.css";

type Rol = "admin" | "vendedor" | "cocinero" | "fabrica" | "contabilidad";

interface UsuarioEntry {
    username: string;
    /** Un usuario puede pertenecer a varios locales. El primero es el activo. */
    local_ids: number[];
    default_local_id: number | null;
    rol: Rol;
}

interface Local {
    locales_id: number;
    nombre_local: string;
}

interface SaveStatus {
    [username: string]: "saving" | "ok" | "error";
}

const ROL_LABELS: Record<Rol, string> = {
    admin:        "Admin",
    vendedor:     "Vendedor",
    cocinero:     "Cocinero",
    fabrica:      "Fábrica",
    contabilidad: "Contabilidad",
};

const ROLE_PERMS: Record<Rol, string[]> = {
    admin: [
        "Todo el panel de administración",
        "Gestión de usuarios y roles",
        "Analíticas, Productos, Categorías",
        "Caja completa (todas las modalidades)",
        "Verificaciones, Promociones",
    ],
    vendedor: [
        "Tomar Pedido",
        "Gestión de Pedidos",
        "Stock",
        "Clientes Fieles",
        "Zonas de Envío",
        "Caja Efectivo",
        "Caja QR",
    ],
    cocinero: [
        "Gestión de Pedidos",
        "Stock",
        "Stock Fábrica (Materia Prima)",
    ],
    fabrica: [
        "Tomar Pedido",
        "Gestión de Pedidos",
        "Stock",
        "Stock Fábrica (Materia Prima)",
        "Clientes Fieles",
        "Zonas de Envío",
        "Caja Efectivo",
        "Caja QR",
    ],
    contabilidad: [
        "Gestión de Pedidos",
        "Analíticas",
        "Verificaciones",
        "Caja Efectivo",
        "Caja Transferencias",
        "Caja Tarjetas",
        "Caja QR",
    ],
};

const ROL_COLOR: Record<Rol, string> = {
    admin:        styles["rol-admin"],
    vendedor:     styles["rol-vendedor"],
    cocinero:     styles["rol-cocinero"],
    fabrica:      styles["rol-fabrica"],
    contabilidad: styles["rol-contabilidad"] || styles["rol-admin"],
};

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const UserManagement: React.FC = () => {
    const { user } = useAuth();
    const [usuarios, setUsuarios] = useState<UsuarioEntry[]>([]);
    const [locales, setLocales] = useState<Local[]>([]);
    const [saveStatus, setSaveStatus] = useState<SaveStatus>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        Promise.all([
            fetch(`${API_URL}/admin/usuarios`).then((r) => r.json()),
            fetch(`${API_URL}/locales`).then((r) => r.json()),
        ])
            .then(([usuariosData, localesData]) => {
                // Tolera respuestas de backends sin local_ids todavia.
                setUsuarios(
                    (Array.isArray(usuariosData) ? usuariosData : []).map((u: UsuarioEntry) => ({
                        ...u,
                        local_ids: Array.isArray(u.local_ids)
                            ? u.local_ids
                            : (u.default_local_id != null ? [u.default_local_id] : []),
                    }))
                );
                setLocales(Array.isArray(localesData) ? localesData : []);
                setLoading(false);
            })
            .catch(() => {
                setError("No se pudieron cargar los usuarios.");
                setLoading(false);
            });
    }, []);

    const nombreLocal = (id: number) =>
        locales.find((l) => l.locales_id === id)?.nombre_local ?? `Local ${id}`;

    const handleLocalesChange = async (username: string, localIds: number[]) => {
        const previos = usuarios.find((u) => u.username === username)?.local_ids ?? [];
        setUsuarios((prev) =>
            prev.map((u) => (u.username === username ? { ...u, local_ids: localIds } : u))
        );
        setSaveStatus((prev) => ({ ...prev, [username]: "saving" }));

        try {
            const res = await fetch(
                `${API_URL}/admin/usuarios/${encodeURIComponent(username)}/locales`,
                {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ local_ids: localIds }),
                }
            );

            if (res.ok) {
                setSaveStatus((prev) => ({ ...prev, [username]: "ok" }));
                setTimeout(
                    () =>
                        setSaveStatus((prev) => {
                            const next = { ...prev };
                            delete next[username];
                            return next;
                        }),
                    2000
                );
            } else {
                // Revertir el update optimista si el backend rechazo el cambio
                setUsuarios((prev) =>
                    prev.map((u) => (u.username === username ? { ...u, local_ids: previos } : u))
                );
                setSaveStatus((prev) => ({ ...prev, [username]: "error" }));
            }
        } catch {
            setUsuarios((prev) =>
                prev.map((u) => (u.username === username ? { ...u, local_ids: previos } : u))
            );
            setSaveStatus((prev) => ({ ...prev, [username]: "error" }));
        }
    };

    const handleRolChange = async (username: string, nuevoRol: Rol) => {
        // Optimistic update
        setUsuarios((prev) =>
            prev.map((u) => (u.username === username ? { ...u, rol: nuevoRol } : u))
        );
        setSaveStatus((prev) => ({ ...prev, [username]: "saving" }));

        try {
            const encodedUsername = encodeURIComponent(username);
            const res = await fetch(`${API_URL}/admin/usuarios/${encodedUsername}/rol`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ rol: nuevoRol }),
            });

            if (res.ok) {
                setSaveStatus((prev) => ({ ...prev, [username]: "ok" }));
                setTimeout(
                    () =>
                        setSaveStatus((prev) => {
                            const next = { ...prev };
                            delete next[username];
                            return next;
                        }),
                    2000
                );
            } else {
                setSaveStatus((prev) => ({ ...prev, [username]: "error" }));
            }
        } catch {
            setSaveStatus((prev) => ({ ...prev, [username]: "error" }));
        }
    };

    if (loading) return <div style={{ padding: 30, opacity: 0.5 }}>Cargando usuarios...</div>;
    if (error) return <div style={{ padding: 30, color: "#ff6b6b" }}>{error}</div>;

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <FaUserShield size={22} style={{ color: "var(--secondary-color)" }} />
                <h3>Gestión de Usuarios y Roles</h3>
            </div>
            <p className={styles.subtitle}>
                Asignale un rol y uno o mas locales a cada usuario. Los cambios se guardan
                inmediatamente.
            </p>

            <div className={styles["table-wrap"]}>
                <table>
                    <thead>
                        <tr>
                            <th>Usuario</th>
                            <th>Locales asignados</th>
                            <th>Rol actual</th>
                            <th>Cambiar rol</th>
                            <th>Estado</th>
                        </tr>
                    </thead>
                    <tbody>
                        {usuarios.map((u) => {
                            const isMe = u.username === user;
                            const status = saveStatus[u.username];
                            return (
                                <tr key={u.username}>
                                    <td>
                                        <div className={styles["username-cell"]}>
                                            {u.username}
                                            {isMe && (
                                                <span className={styles["you-badge"]}>vos</span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        {/*
                                          * Se muestra el nombre del local, nunca su ID.
                                          * El primero de la lista es el predeterminado.
                                          */}
                                        <div className={styles["locales-cell"]}>
                                            {locales.length === 0 && <span>—</span>}
                                            {locales.map((l) => {
                                                const checked = u.local_ids.includes(l.locales_id);
                                                return (
                                                    <label
                                                        key={l.locales_id}
                                                        className={styles["local-option"]}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            disabled={status === "saving"}
                                                            onChange={() =>
                                                                handleLocalesChange(
                                                                    u.username,
                                                                    checked
                                                                        ? u.local_ids.filter(
                                                                              (id) => id !== l.locales_id
                                                                          )
                                                                        : [...u.local_ids, l.locales_id]
                                                                )
                                                            }
                                                        />
                                                        <span>{l.nombre_local}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                        {u.local_ids.length > 0 && (
                                            <small className={styles["local-default-hint"]}>
                                                Predeterminado: {nombreLocal(u.local_ids[0])}
                                            </small>
                                        )}
                                    </td>
                                    <td>
                                        <span
                                            className={`${styles["rol-badge"]} ${ROL_COLOR[u.rol]}`}
                                        >
                                            {ROL_LABELS[u.rol]}
                                        </span>
                                    </td>
                                    <td>
                                        <select
                                            className={styles["rol-select"]}
                                            value={u.rol}
                                            onChange={(e) =>
                                                handleRolChange(u.username, e.target.value as Rol)
                                            }
                                            disabled={status === "saving"}
                                        >
                                            <option value="admin">Admin</option>
                                            <option value="vendedor">Vendedor</option>
                                            <option value="cocinero">Cocinero</option>
                                            <option value="fabrica">Fábrica</option>
                                            <option value="contabilidad">Contabilidad</option>
                                        </select>
                                    </td>
                                    <td>
                                        {status === "saving" && (
                                            <span className={styles["saving-indicator"]}>
                                                Guardando...
                                            </span>
                                        )}
                                        {status === "ok" && (
                                            <span className={styles["msg-ok"]}>
                                                <FaCheck /> Guardado
                                            </span>
                                        )}
                                        {status === "error" && (
                                            <span className={styles["msg-err"]}>Error</span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Leyenda de permisos */}
            <div className={styles.legend}>
                <h4>Permisos por rol</h4>
                <div className={styles["legend-grid"]}>
                    {(["admin", "vendedor", "cocinero", "fabrica", "contabilidad"] as Rol[]).map((r) => (
                        <div key={r} className={styles["legend-role"]}>
                            <div
                                className={`${styles["legend-role-name"]} ${styles["rol-badge"]} ${ROL_COLOR[r]}`}
                            >
                                {ROL_LABELS[r]}
                            </div>
                            <ul>
                                {ROLE_PERMS[r].map((p) => (
                                    <li key={p}>{p}</li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default UserManagement;